/**
 * Base API Service
 * Provides HTTP client with retry logic, timeout, error handling, and cancellation support
 */

import type { ErrorResponse } from '@refinevoice/shared';

/**
 * API request options
 */
export interface ApiRequestOptions extends RequestInit {
  /** Request timeout in milliseconds (default: 10000ms) */
  timeout?: number;
  /** Number of retry attempts (default: 3) */
  retries?: number;
  /** Abort signal for request cancellation */
  signal?: AbortSignal;
}

/**
 * API error with additional context
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public response?: Response
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Base API service class with retry logic, timeout, and error handling
 */
export class ApiService {
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: Used in default parameter destructuring
  private readonly DEFAULT_TIMEOUT = 15000; // 15 seconds
  // biome-ignore lint/correctness/noUnusedPrivateClassMembers: Used in default parameter destructuring
  private readonly DEFAULT_RETRIES = 1;
  private readonly RETRY_DELAYS = [200]; // Single retry with 200ms delay

  /**
   * Make an API request with retry logic and timeout
   *
   * @param endpoint - API endpoint (e.g., '/api/token')
   * @param options - Request options including timeout, retries, and signal
   * @returns Parsed JSON response
   * @throws ApiError on request failure
   */
  async request<T>(endpoint: string, options: ApiRequestOptions = {}): Promise<T> {
    const {
      timeout = this.DEFAULT_TIMEOUT,
      retries = this.DEFAULT_RETRIES,
      signal,
      ...fetchOptions
    } = options;

    let lastError: Error | null = null;

    // Attempt request with retries
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await this.fetchWithTimeout(
          endpoint,
          {
            ...fetchOptions,
            signal,
          },
          timeout
        );

        // Handle HTTP errors
        if (!response.ok) {
          await this.handleErrorResponse(response);
        }

        // Parse and return success response
        return (await response.json()) as T;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));

        // Don't retry on abort or non-retryable errors
        if (this.isNonRetryableError(lastError)) {
          throw lastError;
        }

        // Don't retry on last attempt
        if (attempt === retries) {
          break;
        }

        // Wait before retry with exponential backoff
        const delay = this.RETRY_DELAYS[attempt] || this.RETRY_DELAYS[this.RETRY_DELAYS.length - 1];
        await this.sleep(delay);
      }
    }

    // All retries failed
    throw lastError || new ApiError('Request failed after all retries');
  }

  /**
   * Fetch with timeout support
   *
   * @param endpoint - API endpoint
   * @param options - Fetch options
   * @param timeout - Timeout in milliseconds
   * @returns Response object
   */
  private async fetchWithTimeout(
    endpoint: string,
    options: RequestInit,
    timeout: number
  ): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    try {
      // Merge abort signals if external signal provided
      const signal = options.signal
        ? this.mergeAbortSignals([options.signal, controller.signal])
        : controller.signal;

      const response = await fetch(endpoint, {
        ...options,
        signal,
      });

      return response;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Handle error response from API
   *
   * @param response - Failed response object
   * @throws ApiError with user-friendly message
   */
  private async handleErrorResponse(response: Response): Promise<never> {
    let errorMessage = `Request failed with status ${response.status}`;
    const statusCode = response.status;

    try {
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        const errorData: ErrorResponse = await response.json();
        errorMessage = errorData.message || errorData.error || errorMessage;
      } else {
        const textError = await response.text();
        if (textError) {
          errorMessage = textError;
        }
      }
    } catch {
      // Failed to parse error response, use default message
    }

    throw new ApiError(this.transformErrorMessage(errorMessage, statusCode), statusCode, response);
  }

  /**
   * Transform API error messages to user-friendly messages
   *
   * @param message - Original error message
   * @param statusCode - HTTP status code
   * @returns User-friendly error message
   */
  private transformErrorMessage(message: string, statusCode: number): string {
    // Map common HTTP status codes to friendly messages
    switch (statusCode) {
      case 400:
        return `Invalid request: ${message}`;
      case 401:
        return 'Authentication failed. Please try again.';
      case 403:
        return 'Access denied. You do not have permission to perform this action.';
      case 404:
        return 'The requested resource was not found.';
      case 408:
        return 'Request timeout. Please check your connection and try again.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      case 502:
      case 503:
      case 504:
        return 'Service temporarily unavailable. Please try again in a moment.';
      default:
        return message;
    }
  }

  /**
   * Check if error should not be retried
   *
   * @param error - Error object
   * @returns True if error is non-retryable
   */
  private isNonRetryableError(error: Error): boolean {
    // Don't retry on abort
    if (error.name === 'AbortError') {
      return true;
    }

    // Don't retry on API errors with specific status codes
    if (error instanceof ApiError) {
      const nonRetryableStatuses = [400, 401, 403, 404, 422];
      return error.statusCode !== undefined && nonRetryableStatuses.includes(error.statusCode);
    }

    return false;
  }

  /**
   * Merge multiple abort signals into one
   *
   * @param signals - Array of abort signals
   * @returns Merged abort signal
   */
  private mergeAbortSignals(signals: AbortSignal[]): AbortSignal {
    const controller = new AbortController();

    for (const signal of signals) {
      if (signal.aborted) {
        controller.abort();
        break;
      }

      signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    return controller.signal;
  }

  /**
   * Sleep for specified duration
   *
   * @param ms - Milliseconds to sleep
   */
  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
